import { Titlebar } from '@components/Titlebar/Titlebar'
import TokenAvatar from '@components/TokenAvatar'
import useToken from '@plug-core/hooks/useToken'
import { EarnPosition, EarnVault } from '@plug-core/interfaces/earn'
import { formatMoney, formatNumber } from '@plug-core/utils/formatNumber'
import { titleize } from '@plug-core/utils/titleize'
import { Button, Heading, ListGroup } from '@ui'
import { ROUTES } from '@utils/routes'
import React, { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { HiArrowUpRight } from 'react-icons/hi2'
import { useNavigate } from 'react-router-dom'
import { InfoRow } from './shared'

interface Props {
  vault: EarnVault
  position?: EarnPosition
  onClose: () => void
}

const formatApy = (value: number) => formatNumber(value, '%', { maximumFractionDigits: 2 })

type Row = { label: string; value: React.ReactNode }

const RowList: FC<{ rows: Row[] }> = ({ rows }) => (
  <ListGroup>
    {rows.map((row) => (
      <InfoRow key={row.label} label={row.label} value={row.value} />
    ))}
  </ListGroup>
)

export const VaultDetails: FC<Props> = ({ vault, position, onClose }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const primaryToken = vault.underlyingTokens[0]
  const tokenLogo = useToken({ address: primaryToken?.address }).logo
  const prettyProtocol = titleize(vault.protocol.name)
  const depositUsd = position?.balanceUsd ? Number(position.balanceUsd) : undefined

  const infoRows: Row[] = [
    { label: t('earn.tvl'), value: formatMoney(Number(vault.analytics.tvl.usd || 0)) },
    { label: t('earn.protocol'), value: prettyProtocol },
    { label: t('earn.chain'), value: vault.network },
    { label: t('earn.asset'), value: primaryToken?.symbol || '—' },
  ]

  if (depositUsd !== undefined) {
    infoRows.push({ label: t('earn.yourDeposit'), value: formatMoney(depositUsd) })
  }

  const apyRows: Row[] = [
    { label: t('earn.baseApy'), value: vault.analytics.apy.base },
    { label: t('earn.rewardApy'), value: vault.analytics.apy.reward },
    { label: '1D', value: vault.analytics.apy1d ?? 0 },
    { label: '7D', value: vault.analytics.apy7d ?? 0 },
    { label: '30D', value: vault.analytics.apy30d ?? 0 },
  ]
    .filter((r) => (r.value as number) > 0)
    .map((r) => ({ label: r.label, value: formatApy(r.value as number) }))

  return (
    <div className="flex flex-col">
      <Titlebar onActionClick={onClose} title={vault.name} />

      <div className="mt-2 flex flex-col items-center gap-2">
        <TokenAvatar size={60} fallbackName={primaryToken?.symbol || vault.name} logo={tokenLogo} />
        <Heading level={2} tone="success" tabular>
          {formatApy(vault.analytics.apy.total)}
        </Heading>
      </div>

      <div className="mt-5 flex flex-col">
        <RowList rows={infoRows} />
      </div>

      {apyRows.length > 0 && (
        <>
          <Heading level={4} className="mt-5 mb-2">
            {t('earn.apy')}
          </Heading>
          <RowList rows={apyRows} />
        </>
      )}

      <div className="mt-5 flex flex-col gap-4">
        <Button
          variant="primary"
          fullWidth
          onClick={() => navigate(ROUTES.EARN_DEPOSIT, { state: { vault } })}
        >
          {t(position ? 'earn.depositMore' : 'earn.startEarning')}
        </Button>

        {position && (
          <Button
            variant="secondary"
            fullWidth
            onClick={() => navigate(ROUTES.EARN_WITHDRAW, { state: { vault, position } })}
          >
            {t('earn.withdraw.title')}
          </Button>
        )}

        {vault.protocol.url && (
          <Button
            variant="secondary"
            startIcon={<HiArrowUpRight />}
            onClick={() => window.open(vault.protocol.url, '_blank', 'noopener,noreferrer')}
            fullWidth
          >
            {t('earn.viewOn', { protocol: prettyProtocol })}
          </Button>
        )}
      </div>
    </div>
  )
}
